import type { TourInput } from './schema';

export const tour: TourInput = {
  title: 'Manuel Gasteiger · software for the built world',
  hero: {
    name: 'Duplex Apartment (NIBS)',
    position: [0, 0, 0],
    rotationY: Math.PI / 2,
    propsLayer: 'duplex-arch',
    raw: 'duplex-arch/raw.json',
    ifc: 'duplex-arch/model.ifc',
    layers: [
      {
        id: 'duplex-arch',
        glb: 'duplex-arch/model.glb',
        props: 'duplex-arch/props.json',
        discipline: 'arch',
        label: 'Architecture',
        tint: '#cfd6e0',
        credit: {
          title: 'Duplex Apartment (Architecture)',
          author: 'buildingSMART / NIBS Common BIM Files',
          licence: 'public sample',
        },
      },
      {
        id: 'duplex-spaces',
        glb: 'duplex-spaces/model.glb',
        props: 'duplex-spaces/props.json',
        discipline: 'space',
        label: 'Rooms',
        tint: '#4cc9f0',
        credit: {
          title: 'Duplex Apartment (Spaces)',
          author: 'buildingSMART / NIBS Common BIM Files',
          licence: 'public sample',
        },
      },
      {
        id: 'duplex-electrical',
        glb: 'duplex-electrical/model.glb',
        props: 'duplex-electrical/props.json',
        discipline: 'mep',
        label: 'Electrical',
        tint: '#f0b429',
        credit: {
          title: 'Duplex Apartment (Electrical)',
          author: 'buildingSMART / NIBS Common BIM Files',
          licence: 'public sample',
        },
      },
      {
        id: 'duplex-mechanical',
        glb: 'duplex-mechanical/model.glb',
        props: 'duplex-mechanical/props.json',
        discipline: 'mep',
        label: 'Mechanical',
        tint: '#4cc9f0',
        credit: {
          title: 'Duplex Apartment (Mechanical)',
          author: 'buildingSMART / NIBS Common BIM Files',
          licence: 'public sample',
        },
      },
      {
        id: 'duplex-plumbing',
        glb: 'duplex-plumbing/model.glb',
        props: 'duplex-plumbing/props.json',
        discipline: 'mep',
        label: 'Plumbing',
        tint: '#7bd88f',
        credit: {
          title: 'Duplex Apartment (Plumbing)',
          author: 'buildingSMART / NIBS Common BIM Files',
          licence: 'public sample',
        },
      },
    ],
  },
  // ?hero=nugget, dev only until the licence is cleared (pipeline/models/README.md)
  heroes: {
    nugget: {
      name: 'Golden Nugget (INNOCAD / Autodesk sample)',
      position: [0, 0, 0],
      rotationY: 0,
      cameraScale: 1.7,
      propsLayer: 'nugget-arch',
      raw: 'nugget-arch/raw.json',
      layers: [
        {
          id: 'nugget-arch',
          glb: 'nugget-arch/model.glb',
          props: 'nugget-arch/props.json',
          discipline: 'arch',
          label: 'Architecture & structure',
          tint: '#cfd6e0',
          credit: {
            title: 'Golden Nugget (Architecture & Structure)',
            author: 'INNOCAD via Autodesk sample content',
            licence: 'unclear, dev only',
          },
        },
        {
          id: 'nugget-spaces',
          glb: 'nugget-spaces/model.glb',
          props: 'nugget-spaces/props.json',
          discipline: 'space',
          label: 'Rooms',
          tint: '#4cc9f0',
          credit: {
            title: 'Golden Nugget (Rooms)',
            author: 'INNOCAD via Autodesk sample content',
            licence: 'unclear, dev only',
          },
        },
        {
          id: 'nugget-mep',
          glb: 'nugget-mep/model.glb',
          props: 'nugget-mep/props.json',
          discipline: 'mep',
          label: 'Building services',
          tint: '#4cc9f0',
          credit: {
            title: 'Golden Nugget (Building Services)',
            author: 'INNOCAD via Autodesk sample content',
            licence: 'unclear, dev only',
          },
        },
      ],
    },
  },
  stations: [
    {
      id: 'intro',
      index: 0,
      kind: 'intro',
      title: 'Manuel Gasteiger',
      nav: 'Intro',
      subtitle: 'Software for the built world · 15 years · Innsbruck',
      body: `Full-stack, BIM and applied AI for construction and regulated industry. Co-founded Sitelife, now senior engineer at Build Informed.

One building, seven stations. Scroll.`,
      skills: [],
      waypoints: [
        { id: 'intro-far', at: 0, position: [0, 5, 55], lookAt: [0, 3, 0] },
        { id: 'intro-near', at: 0.1, position: [14, 7, 26], lookAt: [0, 3, 0] },
      ],
    },
    {
      id: 'revit',
      index: 1,
      kind: 'revit',
      title: 'Open Revit without Revit',
      nav: 'Revit',
      subtitle: '.rvt and .rfa read in your browser · no Revit, no upload',
      body: `I read Revit files in your browser: file info, the preview image, the element table and the geometry.

Walls, floors, roofs, curtain walls and placed families come back as solids, every other model element as its bounding box. Revit 2024 to 2026 reads fully, older files give info and preview.

Drop your own .rvt or .rfa on the card. It never leaves this page.`,
      skills: ['bim', 'fullstack'],
      position: [34, 0, 0],
      waypoints: [
        { id: 'revit-approach', at: 0.14, position: [12, 24, 66], lookAt: [26, 3, 2] },
        { id: 'revit-orbit', at: 0.18, position: [74, 16, 34], lookAt: [32, 3, 4] },
        { id: 'revit-close', at: 0.22, position: [46, 7, 28], lookAt: [31, 3, 8] },
      ],
    },
    {
      id: 'assistant',
      index: 2,
      kind: 'build',
      title: 'Ask and build',
      nav: 'Ask',
      subtitle: 'A language model in your browser · tools over the IFC · MCP',
      project: {
        name: 'Applied AI',
        client: 'Build Informed',
        years: '2025–2026',
        stack: ['transformers.js', 'WebGPU', 'tool calling', 'MCP servers', 'Claude Code skills'],
      },
      body: `I run a small language model in your browser. It answers with tool calls over the IFC, served over MCP in this page and to Claude Desktop.

It builds too, by command: a storey, a roof, stairs, walls, openings, and it takes them out again. The commands run as recipes that never guess; the model only answers questions. Plug the same tools into Claude Desktop and it designs. Every change is checked afterwards and you get the edited .ifc.

Quantities come as a table and as an Excel file, one sheet each for material, type and storey.

Every tool call is shown under the answer. No server, no API key.`,
      skills: ['ai', 'fullstack'],
      waypoints: [
        { id: 'assistant-approach', at: 0.26, position: [-32, 14, 18], lookAt: [0, 3, 0] },
        { id: 'assistant-run', at: 0.32, position: [-18, 10, 14], lookAt: [-2, 3, 0] },
      ],
    },
    {
      id: 'compare',
      index: 3,
      kind: 'compare',
      title: 'Compare versions',
      nav: 'Compare',
      subtitle: 'Original against edited · matched by GlobalId',
      body: `I diff the model you just edited against the file it came from. Every element is matched by its GlobalId: added in green, removed in red, changed in cyan.

Drag the slider to wipe between before and after.`,
      skills: ['bim', 'fullstack'],
      waypoints: [
        { id: 'compare-approach', at: 0.38, position: [8, 16, 32], lookAt: [0, 5, 0] },
        { id: 'compare-run', at: 0.44, position: [5, 13, 27], lookAt: [0, 5.5, 0] },
      ],
    },
    {
      id: 'drawings',
      index: 4,
      kind: 'drawings',
      title: 'Drawings from the model',
      nav: 'Drawings',
      subtitle: 'Floor plans and sections cut from the IFC, in your browser',
      project: {
        name: 'Drawings',
        years: '2026',
        stack: ['ifc-lite', 'Web Worker', 'SVG', 'DXF'],
      },
      body: `I cut the model 1.2 m above a storey and draw what the cut hits, every room named and measured.

A section slides through the building the same way. Both download as SVG and DXF.

It is the same in-memory model the assistant edits: a storey built there shows up here in its own plan.`,
      skills: ['bim', 'fullstack'],
      waypoints: [
        { id: 'drawings-approach', at: 0.5, position: [6, 34, 22], lookAt: [2, 0, 0] },
        { id: 'drawings-top', at: 0.55, position: [4, 30, 11], lookAt: [3, 0, 0] },
      ],
    },
    {
      id: 'rules',
      index: 5,
      kind: 'rules',
      title: 'Check against rules',
      nav: 'Rules',
      subtitle: 'IDS in the browser · pass and fail on the model',
      project: {
        name: 'Raumbuch add-in',
        client: 'VAMED',
        years: '2025–2026',
        stack: ['IDS 1.0', '@ifc-lite/ids', 'Web Worker', 'F#', 'Revit API'],
      },
      body: `IDS is the buildingSMART standard for information requirements. I run the check in your browser, on the model you see, floors you built included.

Pick a rule set or drop your own .ids. Red fails, green passes; click a row to find the element.

The DIN 13080 set is the room-book check I built as a Revit add-in, now as an open file.`,
      skills: ['bim', 'csharp-fsharp'],
      waypoints: [
        { id: 'rules-approach', at: 0.61, position: [-26, 10, 24], lookAt: [0, 3, 0] },
        { id: 'rules-run', at: 0.65, position: [-16, 7, 20], lookAt: [-2, 3, 0] },
        { id: 'rules-close', at: 0.68, position: [-4, 4, 15], lookAt: [-4, 2.5, 3] },
      ],
    },
    {
      id: 'world',
      index: 6,
      kind: 'world',
      title: 'Place it in the world',
      nav: 'World',
      subtitle: 'IfcSite, true north and the sun over Innsbruck',
      body: `An IFC knows where it stands: IfcSite carries latitude and longitude, the model context carries true north.

From those and a date I put the sun where it stands over Innsbruck, and the building casts its shadow on the neighbours, baked from OpenStreetMap.

Scroll to run the day, or pick a date and an hour.`,
      skills: ['bim', 'fullstack'],
      waypoints: [
        { id: 'world-approach', at: 0.74, position: [62, 74, 92], lookAt: [0, 2, 0] },
        { id: 'world-street', at: 0.8, position: [-28, 3.4, 14], lookAt: [0, 5, 0] },
        { id: 'world-orbit', at: 0.84, position: [-26, 3.4, -16], lookAt: [0, 5, 0] },
      ],
    },
    {
      id: 'projects',
      index: 7,
      kind: 'projects',
      title: 'Projects',
      nav: 'Projects',
      body: `The projects behind these stations, and the open work I built on.`,
      skills: ['leadership'],
      waypoints: [
        { id: 'projects-pull', at: 0.91, position: [28, 45, 40], lookAt: [0, 0, 0] },
        { id: 'projects-plan', at: 1, position: [0, 150, 55], lookAt: [0, 0, -8] },
      ],
    },
  ],
};
